import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { FormFieldGrid, FormFieldReadout } from "../components/FormFields";
import type { FormValues } from "../components/FormFields";
import { RaiseFormModal } from "./ProjectFormsPage";
import { useTenant } from "../tenant";
import type { FormsMeta, FormType, ProjectDetail, ProjectFormDetail } from "../types";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function tone(t: string): "good" | "warn" | "danger" | "accent" | "neutral" {
  if (t === "good") return "good";
  if (t === "warn") return "warn";
  if (t === "bad") return "danger";
  if (t === "info") return "accent";
  return "neutral";
}

export function FormDetailPage() {
  const { id = "", formId = "" } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const tenant = useTenant();
  const navigate = useNavigate();
  const [form, setForm] = useState<ProjectFormDetail | null>(null);
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [catalog, setCatalog] = useState<FormType[]>([]);
  const [meta, setMeta] = useState<FormsMeta | null>(null);
  const [followOn, setFollowOn] = useState<FormType | null>(null);
  const [error, setError] = useState("");

  // action panel state
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [stepValues, setStepValues] = useState<FormValues>({});
  const [revision, setRevision] = useState<FormValues>({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    Promise.all([
      endpoints.getForm(id, formId),
      endpoints.getProject(id),
      endpoints.formsCatalog(),
      endpoints.formsMeta(id),
    ])
      .then(([f, p, c, m]) => {
        setForm(f);
        setProject(p);
        setCatalog(c);
        setMeta(m);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id, formId]);

  /**
   * The forms this one leads to — a question that reveals a change, a rejected
   * material that needs an alternative, an accident that follows an unsafe act.
   *
   * The catalogue has always declared these chains and nothing could walk them:
   * you had to know that the answer to an RFI belongs in an RFC, go back to the
   * register, and raise it unlinked. Raised from here it carries the parent, so
   * the register shows the trail between them.
   */
  const followOns = useMemo(() => {
    if (!form || !meta) return [];
    return form.spawns
      .map((code) => catalog.find((c) => c.code === code))
      .filter((spec): spec is FormType => Boolean(spec))
      .filter((spec, i, all) => all.findIndex((x) => x.code === spec.code) === i);
  }, [form, meta, catalog]);

  const mine = followOns.filter((spec) => meta?.canRaise.includes(spec.code));
  const theirs = followOns.filter((spec) => !meta?.canRaise.includes(spec.code));

  async function act(body: Parameters<typeof endpoints.actOnForm>[2], label: string) {
    setBusy(label);
    try {
      const next = await endpoints.actOnForm(id, formId, body);
      setForm(next);
      setComment("");
      setInternal(false);
      setStepValues({});
      setRevision({});
      toast.success(t("forms.recorded"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  async function addFile(file: File) {
    setBusy("upload");
    try {
      setForm(await endpoints.uploadAttachment(id, formId, file, internal));
      toast.success(t("files.added", { name: file.name }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  async function removeFile(attachmentId: string) {
    try {
      setForm(await endpoints.deleteAttachment(id, formId, attachmentId));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!form)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  const closed = form.status === "closed";

  return (
    <div className="page">
      <div className="crumb">
        <Link to={`/projects/${id}`}>{t("projects.title")}</Link>{" "}
        <span className="muted">/ </span>
        <Link to={`/projects/${id}/forms`}>{t("forms.title")}</Link>{" "}
        <span className="muted">/ {form.number}</span>
      </div>

      <header className="page__head between">
        <div>
          <h1 className="page__title">
            <span className="mono">{form.number}</span>
            {form.rev > 0 && <span className="muted"> · rev {form.rev}</span>}
          </h1>
          <p className="page__sub">
            {form.title} — {form.subject}
          </p>
        </div>
        <div className="form-state">
          {closed ? (
            <Badge tone={tone(form.outcomeTone)}>{form.outcomeLabel || t("forms.closed")}</Badge>
          ) : (
            <>
              <Badge tone={form.overdue ? "danger" : "accent"}>{form.stepLabel}</Badge>
              <div className="u-mail">
                {form.canAct
                  ? t("forms.actionYou")
                  : t("forms.waitingOn", { party: form.pendingPartyLabel })}
              </div>
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => window.print()}>
            {t("forms.print")}
          </Button>
        </div>
      </header>

      {/*
        The issued document. Hidden on screen, and the only thing that survives
        into print — a consultant still has to hand somebody a piece of paper,
        and it must identify the project, the form and its revision without the
        app's chrome around it.
      */}
      <div className="printdoc" aria-hidden="true">
        <div className="printdoc__head">
          <div>
            <div className="printdoc__co">{tenant.name}</div>
            <div className="printdoc__proj">
              {project?.name}
              {project?.code ? ` · ${project.code}` : ""}
            </div>
          </div>
          <div className="printdoc__id">
            <div className="printdoc__no mono">{form.number}</div>
            <div>
              {t("forms.printRev", { rev: form.rev })} ·{" "}
              {closed ? form.outcomeLabel || t("forms.closed") : form.stepLabel}
            </div>
          </div>
        </div>
        <h2 className="printdoc__title">
          {form.title} — {form.subject}
        </h2>
      </div>

      <div className="detail-grid">
        <Card title={t("forms.details")} className="grid__wide">
          <div className="form-meta">
            <span>
              {t("forms.raisedBy")}: <strong>{form.originatorName}</strong>
              {form.originatorGroupName && <span className="muted"> · {form.originatorGroupName}</span>}
            </span>
            <span className="muted">{new Date(form.createdAt).toLocaleString()}</span>
            {form.dueAt && (
              <span className={form.overdue ? "form-due is-late" : "form-due"}>
                {t("forms.due")}: {new Date(form.dueAt).toLocaleDateString()}
              </span>
            )}
            <span className="muted">{t("forms.filedIn", { folder: form.folder })}</span>
          </div>
          <FormFieldReadout fields={form.fields} values={form.data} />
        </Card>

        <Card title={t("files.title")} subtitle={t("files.hint")} className="grid__wide">
          {form.attachments.length === 0 ? (
            <p className="muted">{t("files.empty")}</p>
          ) : (
            <ul className="files">
              {form.attachments.map((a) => (
                <li className={a.internal ? "file is-internal" : "file"} key={a.id}>
                  <a
                    className="file__name"
                    href={endpoints.attachmentUrl(id, formId, a.id)}
                    download={a.filename}
                  >
                    {a.filename}
                  </a>
                  <span className="file__meta">
                    {formatSize(a.sizeBytes)} · {a.uploaderName} ({a.uploaderPartyLabel}) ·{" "}
                    {new Date(a.createdAt).toLocaleDateString()}
                    {a.rev > 0 && ` · rev ${a.rev}`}
                  </span>
                  {a.internal && <Badge tone="warn">{t("forms.internal")}</Badge>}
                  {a.canDelete && (
                    <button
                      className="rec__del"
                      aria-label={t("common.delete")}
                      onClick={() => void removeFile(a.id)}
                    >
                      ✕
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {!closed && (
            <div className="files__add">
              <label className="ax-btn ax-btn--outline ax-btn--sm">
                {busy === "upload" ? t("files.uploading") : t("files.add")}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void addFile(f);
                  }}
                />
              </label>
              {form.isInternalParty && (
                <button
                  type="button"
                  className={internal ? "chip is-on" : "chip"}
                  onClick={() => setInternal((v) => !v)}
                >
                  {t("files.internal")}
                </button>
              )}
              <span className="muted">{t("files.limit")}</span>
            </div>
          )}
        </Card>

        {/* What this party can do right now. */}
        {form.canAct && !closed && (
          <Card title={t("forms.yourAction")} className="grid__wide form-action-card">
            {form.canResubmit ? (
              <>
                <p className="muted">{t("forms.reviseHint")}</p>
                <FormFieldGrid
                  fields={form.fields}
                  values={{ ...form.data, ...revision }}
                  onChange={(k, v) => setRevision((s) => ({ ...s, [k]: v }))}
                />
                <Field label={t("forms.comment")}>
                  <textarea
                    className="ax-input ax-textarea"
                    rows={2}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </Field>
                <div className="brand-actions">
                  <Button
                    loading={busy === "resubmit"}
                    onClick={() => void act({ resubmit: true, data: revision, comment }, "resubmit")}
                  >
                    {t("forms.resubmit")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {form.stepFields.length > 0 && (
                  <FormFieldGrid
                    fields={form.stepFields}
                    values={stepValues}
                    onChange={(k, v) => setStepValues((s) => ({ ...s, [k]: v }))}
                  />
                )}
                <Field label={t("forms.comment")} hint={t("forms.commentHint")}>
                  <textarea
                    className="ax-input ax-textarea"
                    rows={2}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </Field>
                {form.isInternalParty && (
                  <button
                    type="button"
                    className={internal ? "chip is-on" : "chip"}
                    onClick={() => setInternal((v) => !v)}
                  >
                    {t("forms.internalNote")}
                  </button>
                )}
                <div className="form-decisions">
                  {form.decisions.map((d) => (
                    <Button
                      key={d.key}
                      variant={d.tone === "good" ? "primary" : "outline"}
                      loading={busy === d.key}
                      onClick={() =>
                        void act({ decision: d.key, comment, internal, data: stepValues }, d.key)
                      }
                    >
                      {d.label}
                    </Button>
                  ))}
                </div>
              </>
            )}
          </Card>
        )}

        {/* Anyone on the form may add a note without moving it. */}
        {!form.canAct && !closed && (
          <Card title={t("forms.comment")} className="grid__wide">
            <p className="muted">{t("forms.waitingOn", { party: form.pendingPartyLabel })}</p>
            <textarea
              className="ax-input ax-textarea"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="brand-actions">
              {form.isInternalParty && (
                <button
                  type="button"
                  className={internal ? "chip is-on" : "chip"}
                  onClick={() => setInternal((v) => !v)}
                >
                  {t("forms.internalNote")}
                </button>
              )}
              <Button
                size="sm"
                variant="outline"
                loading={busy === "comment"}
                disabled={!comment.trim()}
                onClick={() => void act({ comment, internal }, "comment")}
              >
                {t("forms.addNote")}
              </Button>
            </div>
          </Card>
        )}

        {followOns.length > 0 && (
          <Card title={t("forms.nextTitle")} className="grid__wide form-next">
            <p className="page__sub">{t("forms.nextHint")}</p>
            {mine.length > 0 && (
              <div className="form-next__row">
                {mine.map((spec) => (
                  <button
                    key={spec.code}
                    type="button"
                    className="nextcard"
                    onClick={() => setFollowOn(spec)}
                  >
                    <span className="mono nextcard__code">{spec.code}</span>
                    <span className="nextcard__title">{spec.title}</span>
                    <span className="nextcard__why">{spec.summary}</span>
                    <span className="nextcard__cta">{t("forms.raiseFollowOn")}</span>
                  </button>
                ))}
              </div>
            )}
            {theirs.length > 0 && (
              <p className="muted form-next__theirs">
                {t("forms.nextNotYours", {
                  forms: theirs.map((s) => `${s.code} — ${s.title}`).join(", "),
                })}
              </p>
            )}
          </Card>
        )}

        <Card title={t("forms.trail")} className="grid__wide">
          <p className="page__sub">{t("forms.trailHint")}</p>
          <ol className="trail">
            {form.actions.map((a) => (
              <li key={a.id} className={a.internal ? "trail__item is-internal" : "trail__item"}>
                <span className={`trail__dot trail__dot--${a.tone || "neutral"}`} />
                <div className="trail__body">
                  <div className="trail__top">
                    <strong>{a.actorName}</strong>
                    <Badge tone="neutral">{a.actorPartyLabel}</Badge>
                    {a.decisionLabel && <Badge tone={tone(a.tone)}>{a.decisionLabel}</Badge>}
                    {a.internal && <Badge tone="warn">{t("forms.internal")}</Badge>}
                    {a.actingAdmin && (
                      <Badge tone="danger">
                        {t("viewAs.viaAdmin", { admin: a.actingAdmin })}
                      </Badge>
                    )}
                  </div>
                  {a.stepLabel && <div className="trail__step">{a.stepLabel}</div>}
                  {a.comment && <p className="trail__comment">{a.comment}</p>}
                  <time className="trail__time mono">{new Date(a.createdAt).toLocaleString()}</time>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      {followOn && (
        <RaiseFormModal
          projectId={id}
          spec={followOn}
          parentFormId={formId}
          // The follow-on almost always concerns the same thing as its parent,
          // so it opens with the subject already carried over rather than blank.
          initialSubject={form.subject}
          onClose={() => setFollowOn(null)}
          onRaised={(raised) => {
            setFollowOn(null);
            toast.success(t("forms.raised", { number: raised.number }));
            navigate(`/projects/${id}/forms/${raised.id}`);
          }}
        />
      )}
    </div>
  );
}
